import { FlatTreeControl } from '@angular/cdk/tree';
import { Component, inject, OnInit, signal } from '@angular/core';
import {
  FormBuilder,
  FormGroup,
  FormsModule,
  ReactiveFormsModule,
} from '@angular/forms';
import { MatCardModule } from '@angular/material/card';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MatExpansionModule } from '@angular/material/expansion';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import {
  MatTreeFlatDataSource,
  MatTreeFlattener,
  MatTreeModule,
} from '@angular/material/tree';
import { FieldType } from '@ngx-formly/material/form-field';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import {
  ItemAttributeKey,
  TenderItemAttributesResponseItem,
  TenderItemAttributesResponseItemAttribute,
  TenderXatSvc,
} from 'net-api-lib';
import { ItemAmountComponent } from 'projects/cupcake/src/app/tender/item/item-amount/item-amount.component';
import { TenderItemsService } from 'projects/cupcake/src/app/tender/item/tender-items.service';
import { debounceTime, startWith } from 'rxjs';
import { SubSink } from 'subsink';

interface ItemWithTreeAndDataSource extends TenderItemAttributesResponseItem {
  treeControl: FlatTreeControl<FlatNode, FlatNode>;
  dataSource: MatTreeFlatDataSource<unknown, FlatNode, FlatNode>;
}

interface FlatNode {
  expandable: boolean;
  name: string;
  level: number;
  oid: number;
  scoreMax?: number;
}

type SelectedItemsAttributes = Record<number, (number | null)[]>;

interface ItemWithTreeAndDataSourceExtended extends ItemWithTreeAndDataSource {
  amount?: number;
  amountUnit?: string | null;
  searchText: string;
}

@Component({
  selector: 'app-formly-item-attribute-tree',
  templateUrl: './formly-item-attribute-tree.component.html',
  styleUrls: ['./formly-item-attribute-tree.component.scss'],
  imports: [
    MatTreeModule,
    MatCheckboxModule,
    MatExpansionModule,
    TranslateModule,
    MatInputModule,
    ReactiveFormsModule,
    FormsModule,
    MatCardModule,
    ItemAmountComponent,
    MatIconModule,
  ],
})
export class FormlyItemAttributeTreeComponent
  extends FieldType<any>
  implements OnInit
{
  private tenderXatSvc = inject(TenderXatSvc);
  private translate = inject(TranslateService);

  private tenderItemsService = inject(TenderItemsService);
  fb = inject(FormBuilder);

  treeObjects = signal<ItemWithTreeAndDataSource[]>([]);
  search = signal<string>(''); // Search Client in tree objects

  searchValue = signal('');

  searchForm: FormGroup = this.fb.group({
    search: [''],
  });

  filteredTreeObjects = this.tenderItemsService.getItemsSignal(
    this.searchValue,
    this.treeObjects
  );

  selectedAttributeOids: SelectedItemsAttributes = {};

  subs = new SubSink();

  private transformer = (
    node: TenderItemAttributesResponseItemAttribute,
    level: number
  ) => {
    return {
      expandable: !!node.itemAttributes && node.itemAttributes.length > 0,
      name: node.name,
      level: level,
      oid: node.oid,
      scoreMax: node.scoreMax,
    };
  };

  ngOnInit() {
    this.loadTreeData();

    // Subscribe to the search form value changes and update the search signal
    this.searchForm
      .get('search')
      ?.valueChanges.pipe(debounceTime(300))
      .subscribe(value => {
        this.search.set(value);
      });
  }

  loadTreeData() {
    // Subscribe to the observable to get the tender item attributes from the service
    this.tenderXatSvc
      .getTenderItemAttributes(this.props.tenderOid)
      .subscribe(res => {
        // Function to process tree objects

        const processTreeObject = (
          item: TenderItemAttributesResponseItem
        ): TenderItemAttributesResponseItem => {
          // Function to transform attributes recursively and calculate scoreMax
          const transformAttributes = (
            attributes: TenderItemAttributesResponseItemAttribute[],
            isRootLevel: boolean
          ): TenderItemAttributesResponseItemAttribute[] => {
            // Calculate the total scoreMax for all attributes at this level
            const totalScoreMax = attributes.reduce(
              (sum, attr) => sum + (attr.scoreMax || 0),
              0
            );

            // if this is the root level and there are attributes, return a single object with total scoreMax and fixed name
            if (isRootLevel && attributes.length > 0) {
              return [
                {
                  oid: null, // use the ItemOid as oid
                  name: this.translate.instant(
                    'TENDER.ATTRIBUTES.GENERAL_ATTRIBUTES'
                  ),
                  scoreMax: totalScoreMax,
                  itemAttributes: [],
                },
              ];
            }

            return (
              attributes
                ?.map(attr => {
                  // Recursively transform child attributes but do not include their scores in this level's scoreMax calculation
                  const childAttributes = transformAttributes(
                    attr.itemAttributes || [],
                    false
                  );

                  // If the attribute type is 'Group', transform the attribute
                  if (attr.type === 'Group') {
                    return {
                      oid: attr.oid,
                      name: attr.name,
                      type: attr.type,
                      scoreMax: attr.scoreMax,
                      itemAttributes: childAttributes, // Recursively transform child attributes
                    };
                  }
                  return null; // Exclude non-Group types
                })
                .filter(attr => attr !== null) || []
            );
          };

          // Transform the attributes at the first level
          // Filter out 'Group' types for the first level attributes, those will be handled separately
          // and include them in the firstLevelGroups
          const firstLevelAttributes = transformAttributes(
            (item.itemAttributes || []).filter(f => f.type !== 'Group'),
            true
          );

          // Transform the attributes at the first level for groups
          // This will include the 'Group' types and their children
          const firstLevelGroups = transformAttributes(
            item.itemAttributes || [],
            false
          );

          // Return the transformed tree object
          return {
            ...item,
            itemAttributes: [...firstLevelAttributes, ...firstLevelGroups],
          };
        };

        // Map the response content to tree objects
        const processedTreeObjects =
          res.content?.items?.map(item => processTreeObject(item)) || [];

        // Initialize tree controls and data sources for each tree object
        const treeObjectsTemp = processedTreeObjects.map(treeObject => {
          const treeControl = new FlatTreeControl<FlatNode>(
            node => node.level,
            node => node.expandable
          );

          const treeFlattener = new MatTreeFlattener(
            this.transformer,
            node => node.level,
            node => node.expandable,
            node => node.itemAttributes
          );

          const dataSource = new MatTreeFlatDataSource(
            treeControl,
            treeFlattener as any
          );
          dataSource.data = treeObject.itemAttributes!;

          // Expand all nodes
          treeControl.expandAll();

          return {
            ...treeObject,
            treeControl,
            dataSource,
          };
        });

        this.treeObjects.set(treeObjectsTemp);

        this.subs.sink = this.formControl.valueChanges
          .pipe(startWith(this.formControl.value))
          .subscribe((value?: ItemAttributeKey[]) => {
            value?.forEach(selection => {
              const disabledAttributes = this.model?.disabledAttributes;

              if (!this.selectedAttributeOids[selection.itemOid!])
                this.selectedAttributeOids[selection.itemOid!] = [];

              this.selectedAttributeOids[selection.itemOid!].push(
                selection.itemAttributeOid ?? null
              );
            });
          });
      });
  }

  hasChild = (_: number, node: FlatNode) => node.expandable;

  // Disabled the Checkbox if other Teams have already selected the same ItemAttribute based on disableItemAttributeGroups
  getDisabled(itemOid: number, nodeOid: number): boolean {
    return this.model.disableItemAttributeGroups?.some(
      (disabled: any) =>
        disabled.itemOid === itemOid && disabled.itemAttributeOid === nodeOid
    );
  }

  onSelect(itemOid: number, attributeOid: number, selected: boolean) {
    if (selected) {
      if (this.selectedAttributeOids[itemOid] === undefined)
        this.selectedAttributeOids[itemOid] = [];

      this.selectedAttributeOids[itemOid].push(attributeOid);
    } else {
      this.selectedAttributeOids[itemOid] = this.selectedAttributeOids[
        itemOid
      ].filter(id => id !== attributeOid);
    }

    this.formControl.setValue(
      Object.entries(this.selectedAttributeOids)
        .map(([itemOid, attributeOids]) =>
          attributeOids.map(itemAttributeOid => ({
            itemOid: Number(itemOid),
            itemAttributeOid: itemAttributeOid,
          }))
        )
        .flat(),
      { emitEvent: false }
    );
  }

  OnDestroy(): void {
    this.subs.unsubscribe();
  }
}
